// Web / Telegram account calls. Passkeys use WebAuthn through @simplewebauthn/browser; inside
// Telegram the signed initData is the login instead (see telegramSignIn).
import { startAuthentication, startRegistration, browserSupportsWebAuthn } from '@simplewebauthn/browser';
import type { AccountUser, PlanInfo, PlanProduct, CheckoutCatalog } from '../types';
import { solvePow } from '../logic/pow';

export const nativeAccount = false;
export const authAvailable = true;
// Inside Telegram this is only the browser's claim: Telegram's in-app browser has known WebAuthn problems,
// so the app lets people try and warns that it cannot be guaranteed (see AccountScreen).
export const passkeysSupported = browserSupportsWebAuthn();

export class ApiError extends Error {
  constructor(public code: string, public retryAfter?: number, public data?: Record<string, unknown>) {
    super(code);
  }
}

async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
  const res = await fetch(`/api${path}`, {
    method,
    credentials: 'same-origin',
    headers: body === undefined ? undefined : { 'content-type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = (await res.json().catch(() => ({}))) as { error?: string; retryAfter?: number } & T;
  if (!res.ok) throw new ApiError(data.error ?? `http_${res.status}`, data.retryAfter, data as Record<string, unknown>);
  return data;
}

/** The user dismissed the browser's passkey prompt: not an error worth showing. */
export const isCancelled = (e: unknown): boolean => (e as { name?: string })?.name === 'NotAllowedError';

export async function fetchMe(): Promise<AccountUser | null> {
  return (await call<{ user: AccountUser | null }>('GET', '/me')).user;
}

export type SignUpPhase = 'check' | 'passkey';

/**
 * Sign-up is deliberately not instant: first a small proof-of-work puzzle (a second or two) that makes
 * scripted mass sign-ups expensive, then the passkey. The server refuses the puzzle outright when this
 * network already made an account this hour (error `account_limit`, with how long to wait).
 */
export async function signUp(displayName: string, onPhase?: (p: SignUpPhase) => void): Promise<{ user: AccountUser; recoveryCodes: string[] }> {
  onPhase?.('check');
  const pow = await call<{ id: string; challenge: string; difficulty: number }>('GET', '/auth/pow');
  const powNonce = await solvePow(pow.challenge, pow.difficulty);
  const { challengeId, options } = await call<{ challengeId: string; options: Parameters<typeof startRegistration>[0]['optionsJSON'] }>('POST', '/auth/register/options', { displayName, powId: pow.id, powNonce });
  onPhase?.('passkey');
  const response = await startRegistration({ optionsJSON: options });
  return call<{ user: AccountUser; recoveryCodes: string[] }>('POST', '/auth/register/verify', { challengeId, response });
}

export async function signIn(): Promise<AccountUser> {
  const { challengeId, options } = await call<{ challengeId: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] }>('POST', '/auth/login/options', {});
  const response = await startAuthentication({ optionsJSON: options });
  return (await call<{ user: AccountUser }>('POST', '/auth/login/verify', { challengeId, response })).user;
}

export async function addPasskey(): Promise<AccountUser> {
  const { challengeId, options } = await call<{ challengeId: string; options: Parameters<typeof startRegistration>[0]['optionsJSON'] }>('POST', '/auth/passkeys/options', {});
  const response = await startRegistration({ optionsJSON: options });
  return (await call<{ user: AccountUser }>('POST', '/auth/passkeys/verify', { challengeId, response })).user;
}

export async function removePasskey(id: string): Promise<AccountUser> {
  return (await call<{ user: AccountUser }>('DELETE', `/auth/passkeys/${encodeURIComponent(id)}`)).user;
}

/** One typed code, or every code found in an uploaded PDF (the server counts that as one attempt). */
export async function recoverySignIn(codes: string | string[]): Promise<AccountUser> {
  const body = Array.isArray(codes) ? { codes } : { code: codes };
  return (await call<{ user: AccountUser }>('POST', '/auth/recovery/login', body)).user;
}

/**
 * The code from the bot's /sync_to_web: links Telegram when signed in, signs in when signed out. If that
 * Telegram already has its own account the server answers merge_required (ApiError.data.other) and the
 * code is sent again with who wins: 'web' or 'telegram'.
 */
export const syncWithCode = (code: string, merge?: 'web' | 'telegram') =>
  call<{ user: AccountUser; linked?: boolean; signedIn?: boolean; merged?: boolean }>('POST', '/auth/sync', merge ? { code, merge } : { code });

export const generateRecoveryCodes = () => call<{ user: AccountUser; codes: string[] }>('POST', '/auth/recovery/generate', {});

export async function signOut(): Promise<void> {
  await call('POST', '/auth/logout', {});
}

export async function telegramSignIn(initData: string): Promise<AccountUser> {
  return (await call<{ user: AccountUser }>('POST', '/auth/telegram', { initData })).user;
}

export async function revokeOtherSessions(): Promise<AccountUser> {
  return (await call<{ user: AccountUser }>('POST', '/auth/sessions/revoke-others', {})).user;
}
export const fetchConfig = () => call<{ telegramBot: string | null; stars: Record<PlanProduct, number> }>('GET', '/auth/config');

// Plans: Super for 2, Family for 10, invites and gifts (cloudflare/src/plans.ts).
export const fetchPlan = () => call<PlanInfo>('GET', '/plan');
export const createInvite = () => call<{ code: string; url: string; expiresAt: number }>('POST', '/plan/invite', {});
export const removeMember = (id: string) => call<PlanInfo>('DELETE', `/plan/members/${encodeURIComponent(id)}`);
export const leavePlan = () => call<PlanInfo>('POST', '/plan/leave', {});
export const redeemCode = (code: string) => call<PlanInfo & { joined?: boolean; gift?: { product: PlanProduct; months: number; until: number } }>('POST', '/plan/redeem', { code });
/** Deletes the account and everything stored for it on the server (needs a recent sign-in). */
export async function deleteAccount(): Promise<void> {
  await call('DELETE', '/me');
}
/** A Telegram Stars invoice link for Chefi Super, for the signed-in account. */
export const starsInvoice = (lang: string, product: PlanProduct, months?: number) => call<{ url: string }>('POST', '/pay/stars/invoice', { lang, product, months });
// Web payments (provider-neutral: cloudflare/src/webpay.ts).
/** Which web provider sells which offers (super, family, gift:<plan>:<months>). */
export type WebPayConfig = { provider: null } | { provider: string; offers: string[] };
export const webPayConfig = () => call<WebPayConfig>('GET', '/pay/web/config');
export const webPayCheckout = (offer: string, currency?: string) => call<{ clientSecret: string; publishableKey: string } | { url: string }>('POST', '/pay/web/checkout', { offer, currency, presentation: 'embedded' });
export const webPayCatalog = () => call<CheckoutCatalog>('GET', '/pay/web/catalog');
export const webPayPortal = () => call<{ url: string }>('POST', '/pay/web/portal', {});
export const webPayChange = (product: PlanProduct, interval?: 'month' | 'year') => call<{ ok: true }>('POST', '/pay/web/change', { product, interval });
export async function unlinkTelegram(): Promise<void> {
  await call('DELETE', '/auth/telegram');
}
